/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import qs from 'qs';
import {
  PPSSortOrder,
  FilterGroupNames,
  PremiumExhibitorPrioritySlots,
  Packages,
} from '../../common/Constants';

const idPrefixRegex = /^\d+:\s/;
const idWithSortOrderPrefixRegex = /^\d+:\d+:\s/;
const SUPPORTED_LEVELS = 5;

const getValueFromValues = (id, values) => {
  if (!values) return '';

  const leafKeys = Object.keys(values);
  for (let i = 0; i < leafKeys.length; i++) {
    if (leafKeys[i].startsWith(`${id}:`)) {
      return leafKeys[i];
    }
  }
  return '';
};

const getValueFromFacet = (id, facets, facetKey) => {
  const value = getValueFromValues(id, facets[facetKey]);
  if (value) return value;

  const facetKeys = Object.keys(facets);
  for (let i = 0; i < facetKeys.length; i++) {
    const fallbackValue = getValueFromValues(id, facets[facetKeys[i]]);
    if (fallbackValue) return fallbackValue;
  }
  return '';
};

const parseQueryString = (queryString) => {
  const parsedQueryString =
    qs.parse(queryString, { arrayLimit: 5000 }).refinementList || '';
  if (!parsedQueryString) return parsedQueryString;

  return parsedQueryString.map((e) =>
    typeof e === 'object' ? Object.values(e) : parsedQueryString,
  );
};

const refineFiltersFromQueryString = (queryString, facets) => {
  const parsedQueryString = parseQueryString(queryString);
  if (!parsedQueryString) return '';

  return parsedQueryString.map((filterArr) =>
    filterArr.reduce((acc, filter) => {
      const tokens = filter.split(':');
      if (tokens[1].startsWith('id-')) {
        const id = tokens[1].split('id-')[1];
        const value = getValueFromFacet(id, facets, tokens[0]);
        acc.push(`${tokens[0]}:${value}`);
      } else {
        acc.push(filter);
      }
      return acc;
    }, []),
  );
};

const markSelectedItems = (data, selectedQueryKeys) => {
  const recursive = (item) => {
    const children = item.answers;
    if (children && children.length > 0) {
      children.forEach((child) => {
        if (
          child.queryKey &&
          child.queryText &&
          selectedQueryKeys.includes(`${child.queryKey}:${child.queryText}`)
        ) {
          child.selectedOnLoad = true;
          item.selectedOnLoad = true;
        }
        recursive(child);
      });
    }
  };

  data.forEach((item) => {
    recursive(item);
  });
  return data;
};

const constructUrlParamsWithSelectedFilters = (
  facetFilters,
  currentUrlQueryParamString,
) =>
  Object.assign(
    {},
    omit(qs.parse(currentUrlQueryParamString), ['refinementList']),
    {
      refinementList: facetFilters,
    },
  );

const convertToArray = (data) => {
  if (typeof data === 'string' && data.length) {
    return JSON.parse(data);
  }
  if (typeof data === 'object') {
    return data;
  }

  return [];
};

const parseFilterJson = (data, configs) => {
  const { nestedKey } = configs;
  const recursive = (item1) => {
    const item = item1;
    if (item[nestedKey] && item[nestedKey].length) {
      item[nestedKey] = convertToArray(item[nestedKey]);
      item[nestedKey] &&
        item[nestedKey].forEach((subItem) => {
          subItem[nestedKey] = convertToArray(subItem[nestedKey]);
          recursive(subItem);
        });
    }
  };

  data.forEach((item) => {
    recursive(item);
  });

  return data;
};

const populateLabel = (data, configs) => {
  const { nestedKey } = configs;
  const recursive = (item) => {
    item.shortDescription = item.shortDescription || item.text;
    if (item[nestedKey] && item[nestedKey].length) {
      item[nestedKey] &&
        item[nestedKey].forEach((subItem) => {
          if (subItem[nestedKey] && subItem[nestedKey].length === 0) {
            subItem.shortDescription = subItem.shortDescription || subItem.text;
          }
          recursive(subItem);
        });
    }
  };

  data.forEach((item) => {
    recursive(item);
  });
  return data;
};

const sanitizeLabel = (label) =>
  label.match(idPrefixRegex) || label.match(idWithSortOrderPrefixRegex)
    ? label.split(': ').slice(1).join(': ')
    : label;

const getSortOrder = (label) =>
  label.match(idWithSortOrderPrefixRegex)
    ? parseInt(label.split(':')[1], 10)
    : -1;

const parseAnswer = (
  question,
  parentAnswer,
  answerText,
  filterData,
  facets,
  level,
  levels,
  count,
  sortOrder,
  ppsKey,
) => {
  if (level > SUPPORTED_LEVELS) return {};
  const answerLevel = `${ppsKey}.${question}.${levels[level]}`;
  const queryLevel = `${ppsKey}.${question}.${levels[level - 1]}`;
  if (facets[answerLevel] === undefined) {
    return {
      text: answerText,
      queryKey: queryLevel,
      queryText: parentAnswer,
      count,
      sortOrder,
    };
  }
  const answers = Object.keys(facets[answerLevel]);
  return {
    text: answerText,
    queryText: parentAnswer,
    queryKey: queryLevel,
    count,
    sortOrder,
    answers: sortBy(
      answers
        .map((answer) => {
          if (sanitizeLabel(answer).startsWith(sanitizeLabel(parentAnswer))) {
            return parseAnswer(
              question,
              answer,
              sanitizeLabel(answer).replace(
                `${sanitizeLabel(parentAnswer)} > `,
                '',
              ),
              filterData,
              facets,
              level + 1,
              levels,
              facets[answerLevel][answer],
              getSortOrder(answer),
              ppsKey,
            );
          }
          return {};
        })
        .filter((answer) => Object.entries(answer).length !== 0),
      [PPSSortOrder, 'queryText'],
    ),
  };
};

const parseQuestion = (
  question,
  filterData,
  facets,
  level,
  levels,
  translatedQuestionText,
  ppsKey,
) => {
  if (level > SUPPORTED_LEVELS) return {};
  const answerLevel = `${ppsKey}.${question}.${levels[level]}`;
  const sortOrderValue = facets[`${ppsKey}.${question}.${PPSSortOrder}`];
  const sortOrder =
    question !== FilterGroupNames.CountryName && sortOrderValue != null
      ? parseInt(Object.keys(sortOrderValue), 10)
      : -1;
  const answers = Object.hasOwn(facets, answerLevel)
    ? Object.keys(facets[answerLevel])
    : [];
  return {
    text: translatedQuestionText || question,
    sortOrder,
    answers: sortBy(
      answers.map((answer) =>
        parseAnswer(
          question,
          answer,
          sanitizeLabel(answer),
          filterData,
          facets,
          level + 1,
          levels,
          facets[answerLevel][answer],
          getSortOrder(answer),
          ppsKey,
        ),
      ),
      [PPSSortOrder, 'queryText'],
    ),
  };
};

const isCheckedLeaf = (item) =>
  typeof item.checked !== 'undefined' && item.checked;

const getFilterQueries = (filterData, config) => {
  const { nestedKey } = config;
  const query = {};
  const appendToQuery = (uniqueId, arrayValues) => {
    query[uniqueId] = query[uniqueId] || [];
    query[uniqueId] = query[uniqueId].concat(arrayValues);
  };

  const isLeaf = (item) => typeof item.checked !== 'undefined';

  const hasInnerSelection = (item) =>
    item.selected.length > 0 ||
    item[nestedKey].filter((child) => isCheckedLeaf(child)).length > 0;

  const innerChildrenFunction = (item, uniqueId) => {
    item[nestedKey].forEach((child) => {
      appendToQuery(uniqueId, [child]);
      if (isLeaf(child)) {
        appendToQuery(uniqueId, [child]);
      } else {
        appendToQuery(uniqueId, child[nestedKey]);
        child[nestedKey].forEach((childItem) =>
          innerChildrenFunction(childItem, uniqueId),
        );
      }
    });
  };

  const innerSelectedFunction = (item, uniqueId) => {
    if (item.selected.length) {
      appendToQuery(uniqueId, item.selected);
      item.selected.forEach((selectedItem) => {
        if (hasInnerSelection(selectedItem)) {
          innerSelectedFunction(selectedItem, uniqueId);
        }
      });
    }
    item[nestedKey].forEach(
      (child) => isCheckedLeaf(child) && appendToQuery(uniqueId, [child]),
    );
  };

  const innerFunction = (item, uniqueId) => {
    if (item.selected.length) {
      appendToQuery(uniqueId, item.selected);
      item.selected.forEach((selectedItem) => {
        if (hasInnerSelection(selectedItem)) {
          innerSelectedFunction(selectedItem, uniqueId);
        }
      });
    }

    item[nestedKey].forEach(
      (child) => isCheckedLeaf(child) && appendToQuery(uniqueId, [child]),
    );
  };

  filterData.forEach((item) => innerFunction(item, item.uniqueId));

  return query;
};

const searchStateToUrl = (searchState) =>
  searchState ? `${window.location.pathname}?${qs.stringify(searchState)}` : '';

const refineSearchState = (searchStateQuery) => {
  const parsedQuery = qs.parse(searchStateQuery);
  return omit(parsedQuery, ['configure', 'hitsPerPage']);
};

const updateUrlWIthFilterRefinements = (facetFilters, queryString) => {
  window.history.replaceState(
    {},
    null,
    `${window.location.pathname}?${qs.stringify(constructUrlParamsWithSelectedFilters(facetFilters, queryString))}`,
  );
};

const getNoOfSlotsAllocated = (packageId) => {
  if (packageId === Packages.gold)
    return window.matchMedia('(max-width: 767px)').matches
      ? PremiumExhibitorPrioritySlots.gold.mobile
      : PremiumExhibitorPrioritySlots.gold.desktopOrTablet;
  if (packageId === Packages.silver)
    return window.matchMedia('(max-width: 767px)').matches
      ? PremiumExhibitorPrioritySlots.silver.mobile
      : PremiumExhibitorPrioritySlots.silver.desktopOrTablet;
  else return 0;
};

module.exports = {
  markSelectedItems,
  constructUrlParamsWithSelectedFilters,
  refineFiltersFromQueryString,
  sanitizeLabel,
  parseFilterJson,
  populateLabel,
  parseQuestion,
  isCheckedLeaf,
  getFilterQueries,
  searchStateToUrl,
  refineSearchState,
  updateUrlWIthFilterRefinements,
  getNoOfSlotsAllocated,
};
